// ============================================================================
// JARVISTRAVEL - NOTIFICATION PREFERENCES API (model-ready seam)
// The three notification toggles in Settings (push / SMS / morning prompt),
// backed by GET/PUT /notifications/preferences (core JAR-368). Local state
// stays the source of truth: demo sessions, an unreachable backend, or a
// malformed payload all degrade to the Dashboard's existing localStorage
// persistence — this seam returns null/false instead of throwing, and never
// logs per-call (JAR-292 console hygiene).
//
// Reads and writes are kept strictly separate: getNotificationPrefs never
// writes. The timezone the server schedules against is refreshed by calling
// syncTimezoneIfNeeded explicitly (see JAR-367 review, M1).
// ============================================================================

import { api, isDemoSession } from './client';
import type { AppSettings } from '@/app/types';

/** The notification slice of AppSettings that also lives server-side. */
export type NotificationPrefs = Pick<
  AppSettings,
  'notifications' | 'smsNotifications' | 'morningPrompt'
>;

/**
 * What a write actually did (JAR-867).
 *
 * `false` could not tell these apart, and the difference decides whether
 * retrying is sensible:
 *
 *  - `saved`       the server holds it.
 *  - `rejected`    the server read the payload and refused it — 400, 413 or 422
 *                  only, see `isPayloadRefusal`. core#399 answers 400 on a
 *                  malformed IANA zone. Sending the same bytes again gets the
 *                  same answer, so a caller that retries is looping.
 *  - `unavailable` no verdict on the payload: transport failure, a 5xx, a demo
 *                  session — and also 401, 403, 404, 429 and the rest of the
 *                  4xx range, which say something about the credential, the
 *                  route or the moment rather than about what we sent.
 *                  Retrying is exactly right.
 *
 * The information was always there — ApiResult carries `status` and
 * ApiRequestError carries `isNetworkError`; the seam flattened it on the way
 * out.
 */
export type NotificationPrefsWriteOutcome = 'saved' | 'rejected' | 'unavailable';

/** Status codes that mean the server read this payload and refused it.
 *
 *  An allow-list rather than a 4xx range, because the two failure modes are not
 *  symmetric. Treating a permanent refusal as transient costs one GET and one
 *  PUT per mount — the JAR-867 waste. Treating a transient failure as permanent
 *  turns zone sync off for the whole session and tells nobody. When a code's
 *  meaning is ambiguous, the cheaper mistake is to ask again. */
function isPayloadRefusal(status: number): boolean {
  return status === 400 || status === 413 || status === 422;
}

/** The AppSettings keys this seam owns — used by the Dashboard to decide
 *  whether a settings patch needs a backend write. */
export const NOTIFICATION_PREF_KEYS = [
  'notifications',
  'smsNotifications',
  'morningPrompt',
] as const satisfies readonly (keyof NotificationPrefs)[];

/** Wire shape of GET/PUT /notifications/preferences. `notifications` (FE) is
 *  `push_notifications` on the wire. */
interface BackendNotificationPrefs {
  morning_prompt: boolean;
  push_notifications: boolean;
  sms_notifications: boolean;
  /** IANA zone the server schedules the 7 AM prompt against. (The 7 PM
   *  evening prompt was retired in JAR-626 — core migration 062 drops the
   *  column.) */
  timezone?: string;
}

/** The browser's IANA timezone ("America/Los_Angeles"), or '' if the runtime
 *  won't tell us. The server needs this to fire a "7 AM" prompt at 7 AM where
 *  the user actually is, and it has to follow them as they travel. */
export function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    return '';
  }
}

function isBackendPrefs(p: unknown): p is BackendNotificationPrefs {
  if (!p || typeof p !== 'object') return false;
  const t = p as Record<string, unknown>;
  return (
    typeof t.morning_prompt === 'boolean' &&
    typeof t.push_notifications === 'boolean' &&
    typeof t.sms_notifications === 'boolean' &&
    // timezone is optional, but when present it has to be a string. Leaving it
    // unchecked let `timezone: 123` through the guard and into a variable
    // typed `string`, where every `tz === lastServerTimezone` comparison then
    // failed and the sync re-PUT on every single call — a write loop caused by
    // the one field the guard did not look at (JAR-367 review, M1).
    (t.timezone === undefined || typeof t.timezone === 'string')
  );
}

function fromBackend(p: BackendNotificationPrefs): NotificationPrefs {
  return {
    notifications: p.push_notifications,
    smsNotifications: p.sms_notifications,
    morningPrompt: p.morning_prompt,
  };
}

/** `timezone` is a parameter rather than a `browserTimezone()` call, because a
 *  caller that has already made a decision about the zone has to be able to
 *  send the zone it decided about (JAR-906). */
function toBackend(p: NotificationPrefs, timezone: string): BackendNotificationPrefs {
  return {
    push_notifications: p.notifications,
    sms_notifications: p.smsNotifications,
    morning_prompt: p.morningPrompt,
    timezone,
  };
}

/** The zone the server last told us it had — set by a successful read, and by
 *  a successful write, so the two never disagree about what the server holds.
 *  Declared above its first use; it sat below `getNotificationPrefs` and worked
 *  only because the function runs after module evaluation (review LOW). */
let lastServerTimezone: string | null = null;

/** The zone the last successful WRITE carried — the client's own word, kept
 *  separately from the server's. The pair exists because they can disagree
 *  forever: a server that canonicalises ("Asia/Calcutta" in, "Asia/Kolkata"
 *  out) echoes a value the browser will never report. Comparing the browser
 *  only against the echo makes that permanent disagreement a permanent
 *  re-sync — a GET+PUT on every Dashboard mount, indefinitely, which is the
 *  M1 write loop rebuilt out of correct parts (core#201 review). Knowing what
 *  we last SENT lets the sync say "the server has heard this exact zone and
 *  answered; repeating it cannot change the answer." */
let lastSentTimezone: string | null = null;

/** The last whole-record write, which the next one waits for. */
let writes: Promise<unknown> = Promise.resolve();

/** Runs a read-then-write of the record once the one before it has finished,
 *  so it reads what that one wrote. The PUT replaces the whole record, so two
 *  that overlap lose the first one's change (JAR-2269). */
function inTurn<T>(write: () => Promise<T>): Promise<T> {
  const turn = writes.then(write);
  writes = turn.catch(() => {});
  return turn;
}

/** The saved prefs from the backend, or null when unavailable (demo session,
 *  backend down, malformed payload) — the caller keeps its local values. */
export async function getNotificationPrefs(): Promise<NotificationPrefs | null> {
  if (isDemoSession()) return null;
  try {
    const result = await api.get<BackendNotificationPrefs>('/notifications/preferences');
    if (result.error || !isBackendPrefs(result.data)) return null;
    lastServerTimezone = result.data.timezone ?? '';
    return fromBackend(result.data);
  } catch {
    return null;
  }
}

/**
 * Push the current browser zone to the server if it has drifted from what the
 * server last reported. Call after a successful `getNotificationPrefs`.
 *
 * This exists because the traveler case is the whole point: someone who set
 * their toggles at home would otherwise keep getting the "today's plan" prompt
 * on home time for the entire trip.
 *
 * It is a separate, explicitly-called function rather than a side effect
 * inside the getter (JAR-367 review, M1): a write hidden in a read is a trap
 * for any future caller that polls prefs or syncs across tabs.
 *
 * It takes no prefs argument, deliberately. It used to accept the caller's
 * snapshot and PUT it back, and the PUT is replace-semantics — so a toggle the
 * user changed in another tab, or on the Settings screen, between the caller's
 * read and this write was silently reverted (review round 2, M2). Reading
 * immediately before writing cannot make that window zero, but it shrinks it
 * from "however long the caller held its copy" to the duration of one request,
 * and a caller can no longer hand this function a snapshot from minutes ago.
 * The window closes properly when the endpoint accepts a partial update;
 * PUT-replaces-everything is what makes a timezone-only change a whole-record
 * rewrite.
 *
 * Known limitation: the zone is per-account, not per-device, so it follows the
 * last device to open the app. Opening the app on a home laptop mid-trip
 * reverts the zone until the phone is opened again. Inherent to reading the
 * zone from the browser; revisit only if it bites in practice.
 *
 * Best-effort by design: a failed sync just means the next prompt lands on the
 * previous zone, so it never blocks or throws.
 */
export async function syncTimezoneIfNeeded(): Promise<void> {
  if (isDemoSession()) return;
  const tz = browserTimezone();
  if (!tz || lastServerTimezone === null || tz === lastServerTimezone) return;
  // The server has already heard this exact zone and answered with its own
  // form. Re-sending the same input cannot produce a different answer — it
  // can only re-run the loop. The moment the browser zone actually changes
  // (the traveler case this function exists for), tz stops matching what was
  // sent and the sync fires again (core#201 review).
  if (tz === lastSentTimezone) return;
  return inTurn(() => writeZone(tz));
}

/** The sync's read and write, run in turn with the toggles' (JAR-2269). */
async function writeZone(tz: string): Promise<void> {
  // Freshest server state as the base for the rewrite, not the caller's.
  const current = await getNotificationPrefs();
  if (!current) return; // read failed; next call tries again

  // The server already refused THIS transition — this browser zone, against
  // this server zone — so repeating it can only be refused again, and without
  // this a refused zone costs a write on every mount for as long as the browser
  // keeps reporting it (JAR-867).
  //
  // AFTER the read, and that ordering is the whole correctness of it (review
  // round 3, inline). lastServerTimezone is only refreshed by a successful read
  // or write. Checking the pair before the GET meant comparing against our own
  // cached copy — so when another device moved the server's zone, nothing local
  // knew, the guard matched, and it returned early. The guard suppressed the
  // one request that could have invalidated it, which is the "never syncs
  // again" failure it was introduced to fix, reached by the other half of the
  // pair. Reading first makes that impossible rather than unlikely: the state
  // the guard compares is always one round trip old at most.
  //
  // The cost is the GET, which is the half of JAR-867's waste that buys
  // something. The PUT — the unbounded retry of a payload already refused — is
  // still suppressed, and that is the one that matters.
  if (refusal && tz === refusal.tz && lastServerTimezone === refusal.serverTz) return;

  // `tz`, explicitly — the zone this call read at entry and made every decision
  // about above.
  //
  // Without it saveNotificationPrefs re-read browserTimezone() when it built
  // the body, on the far side of the `await` for the GET. A device crossing a
  // boundary in that window, or a DST transition landing inside it, sent one
  // zone and recorded the refusal against another: the zone that was actually
  // refused kept being retried, and one that was never sent was suppressed
  // (JAR-906).
  //
  // Passing it removes the second read rather than reporting on it, so the
  // guards, the payload and the record are all the same string by construction.
  // A zone that moves mid-flight is not lost — it simply no longer matches
  // lastServerTimezone, so the next call syncs it.
  if ((await saveNotificationPrefs(current, tz)) === 'rejected') {
    // lastServerTimezone is what the GET above just read, so this pair is the
    // exact situation the server refused — not a stale guess at it.
    refusal = { tz, serverTz: lastServerTimezone };
  }
}

/** A refusal, as the PAIR that caused it: the browser zone we sent, and what
 *  the server held at the time.
 *
 *  Distinct from lastSentTimezone, which is only set by a successful write — so
 *  before JAR-867 a rejected zone was recorded nowhere, the "we already sent
 *  that" guard could not see it, and the sync re-read and re-PUT on every
 *  Dashboard mount forever against a value the server had already refused.
 *
 *  It records the server zone as well because a refusal is a fact about a
 *  transition, not about a zone (review M2). Keyed on the browser zone alone it
 *  outlived its own cause: server holds LA, traveler in Rome is refused once,
 *  flies to Tokyo and syncs cleanly, comes back to Rome — and the guard still
 *  matched, so the sync never fired again and prompts stayed on Tokyo time. The
 *  server had moved underneath the refusal and nothing noticed. With the pair,
 *  either half moving means this is a different question and we ask it.
 *
 *  The pair is only worth anything if it is compared against CURRENT state,
 *  which is why syncTimezoneIfNeeded reads before it checks. serverTz here is
 *  whatever the last successful read or write saw; comparing a stale copy of it
 *  reintroduced the same never-syncs-again bug through the server-side half.
 *
 *  One slot, not a set: the browser reports one zone at a time, and a set would
 *  grow without a reader. */
let refusal: { tz: string; serverTz: string | null } | null = null;

/** Test seam: forget the recorded server zone, and any write still queued, so
 *  each case starts clean. */
export function resetTimezoneSyncState(): void {
  lastServerTimezone = null;
  lastSentTimezone = null;
  refusal = null;
  writes = Promise.resolve();
}

/**
 * Change some toggles and leave the rest as core holds them (JAR-2269).
 *
 * The PUT replaces the whole record, so a toggle that sent the page's copy of
 * all three saved whatever that copy held for the other two: the defaults when
 * core's copy hadn't been read, or another device's older choice in a tab left
 * open. This reads core's record and writes it back with only `changes`
 * applied, as syncTimezoneIfNeeded does for the zone, and in turn with it, so
 * two quick toggles each build on the other. A failed read writes nothing.
 */
export function updateNotificationPrefs(changes: Partial<NotificationPrefs>): Promise<NotificationPrefsWriteOutcome> {
  return inTurn(async () => {
    const current = await getNotificationPrefs();
    return current ? saveNotificationPrefs({ ...current, ...changes }) : 'unavailable';
  });
}

/** Persist all toggles (PUT replaces the record). Best-effort: reports what
 *  the backend did with it; local state wins either way. */
export async function saveNotificationPrefs(
  prefs: NotificationPrefs,
  timezone: string = browserTimezone(),
): Promise<NotificationPrefsWriteOutcome> {
  if (isDemoSession()) return 'unavailable';
  const body = toBackend(prefs, timezone);
  try {
    const result = await api.put<BackendNotificationPrefs>('/notifications/preferences', body);
    if (result.error) {
      // `rejected` means the server READ THE PAYLOAD AND REFUSED IT, so sending
      // the same bytes again gets the same answer. That is a much smaller set
      // than "4xx" (review M1), and the difference is not academic: the caller
      // uses `rejected` to stop asking, so mistaking a transient failure for a
      // verdict silently disables the feature for the rest of the session.
      //
      // 401 is the one that proves the point. client.ts:308 refreshes and
      // retries a 401 for reads ONLY — replaying a write risks double-applying
      // it — so an expired session reaches us as a plain 401, and a cold-load
      // race reaches us as 401/NO_CREDENTIAL. Neither judged the payload, and
      // both fix themselves on the next mount.
      //
      // Excluded on purpose, with a reason each: 401/407 (credential, retried
      // by signing in), 403 (permission, granted later), 404/405/410 (route
      // missing — a canary or a rollback mid-deploy), 408/425/429 (explicitly
      // "try again"), 409 (a concurrent write, not this payload).
      return isPayloadRefusal(result.status) ? 'rejected' : 'unavailable';
    }
    // Record what the server now holds. Every write carries the browser zone
    // via toBackend, so a successful PUT is as authoritative about the server's
    // zone as a read is. Without this, syncTimezoneIfNeeded kept comparing
    // against the pre-sync value and re-wrote on the next call, and a direct
    // Settings save never updated it at all (review round 2, M3).
    //
    // Take the value the server echoed over the one we sent (JAR-853). A server
    // that normalises or substitutes the zone would otherwise leave us holding a
    // value it never stored, and every later comparison would be against that
    // fiction — the sync would think it was done while the prompt fired on the
    // wrong clock. When the response carries no zone (the field is optional on
    // the wire) it has told us nothing, so what we sent stays the best estimate;
    // recording '' there would re-PUT on every call, which is the M1 write loop
    // by another route.
    // `||`, not `??`: a server echoing `timezone: ""` has told us nothing,
    // exactly like one omitting the field, so the sent value stays the better
    // record (core#201 review). Honesty note: with the sent-zone guard above,
    // this choice is no longer independently observable — every path where ||
    // and ?? would differ is already stopped by "we sent that exact zone".
    // It stays because it records the truer value, not because a test can
    // pin it; the loop CLASS is pinned by the non-convergent-echo test.
    const echoed = isBackendPrefs(result.data) ? result.data.timezone : undefined;
    lastServerTimezone = echoed || body.timezone || '';
    lastSentTimezone = body.timezone || null;
    return 'saved';
  } catch {
    return 'unavailable';
  }
}
